import React, { useMemo, useState } from 'react'
import {
  AppBar,
  Box,
  Button,
  Divider,
  Drawer,
  IconButton,
  List,
  ListItem,
  ListItemButton,
  ListItemIcon,
  ListItemText,
  Toolbar,
  Typography,
  useMediaQuery
} from '@mui/material'
import { useTheme } from '@mui/material/styles'
import {
  Menu as MenuIcon,
  Home as HomeIcon,
  ExitToApp,
  Person,
  Event,
  Receipt,
  BarChart,
  Report as ReportIcon,
  Inventory2,
  Paid,
  WorkspacePremium,
  LocalShipping
} from '@mui/icons-material'
import { Navigate, Outlet, useLocation, useNavigate } from 'react-router-dom'
import { useAuth } from '../../contexts/AuthContext'
import { useWarzoneCoins } from '../../contexts/WarzoneCoinsContext'
import { usePalworldPerfil } from '../../contexts/PalworldPerfilContext'
import { WARZONE_COIN_IMAGE_URL } from '../../properties'

const drawerWidth = 280
const formatCoins = (value: number) => value.toLocaleString('pt-BR', { minimumFractionDigits: 2, maximumFractionDigits: 2 })

const UserDashboardLayout: React.FC = () => {
  const theme = useTheme()
  const isMobile = useMediaQuery(theme.breakpoints.down('md'))
  const [mobileOpen, setMobileOpen] = useState(false)

  const navigate = useNavigate()
  const location = useLocation()
  const { user, logout } = useAuth()
  const { saldo, isLoading: isSaldoLoading } = useWarzoneCoins()
  const { perfil } = usePalworldPerfil()

  const isAdmin = (user?.roles ?? []).some((r) => ['admin', 'administrator'].includes(String(r).toLowerCase()))
  if (isAdmin) {
    return <Navigate to="/admin" replace />
  }

  const menuItems = useMemo(
    () => [
      { text: 'Meu Perfil', icon: <Person />, path: '/dashboard/perfil' },
      { text: 'Warzone Coins', icon: <Paid />, path: '/dashboard/coins' },
      { text: 'Eventos', icon: <Event />, path: '/dashboard/eventos' },
      { text: 'Meus Pedidos', icon: <Receipt />, path: '/dashboard/pedidos' },
      { text: 'Itens pendentes de entrega', icon: <LocalShipping />, path: '/dashboard/itens-pendentes-entrega' },
      { text: 'Itens', icon: <Inventory2 />, path: '/dashboard/itens' },
      { text: 'Estatísticas', icon: <BarChart />, path: '/dashboard/estatisticas' },
      { text: 'Denúncias', icon: <ReportIcon />, path: '/dashboard/denuncias' }
    ],
    []
  )

  const toggleDrawer = () => setMobileOpen((v) => !v)

  const goHome = () => {
    navigate('/')
    if (isMobile) setMobileOpen(false)
  }

  const doLogout = () => {
    logout()
    navigate('/')
    if (isMobile) setMobileOpen(false)
  }

  const go = (path: string) => {
    navigate(path)
    if (isMobile) setMobileOpen(false)
  }

  const drawer = (
    <Box sx={{ height: '100%', bgcolor: '#101225', display: 'flex', flexDirection: 'column' }}>
      <Box sx={{ p: 3, borderBottom: '1px solid rgba(255,255,255,0.08)' }}>
        <Typography variant="h6" sx={{ fontWeight: 900 }}>
          Área do Cliente
        </Typography>
        <Typography variant="body2" sx={{ color: 'text.secondary', mt: 0.5 }}>
          Olá, {user?.username}
        </Typography>

        <Box
          onClick={() => go('/dashboard/coins')}
          role="button"
          tabIndex={0}
          onKeyDown={(e) => {
            if (e.key === 'Enter' || e.key === ' ') go('/dashboard/coins')
          }}
          sx={{
            mt: 2,
            p: 1.25,
            borderRadius: 2,
            display: 'flex',
            alignItems: 'center',
            gap: 1.25,
            bgcolor: 'rgba(0,230,118,0.10)',
            border: '1px solid rgba(0,230,118,0.20)',
            cursor: 'pointer',
            userSelect: 'none',
            '&:hover': { bgcolor: 'rgba(0,230,118,0.14)' }
          }}
        >
          <Box
            component="img"
            src={WARZONE_COIN_IMAGE_URL}
            alt="Warzone Coin"
            sx={{ width: 20, height: 20, opacity: 0.95 }}
            loading="lazy"
          />
          <Box sx={{ flex: 1, minWidth: 0 }}>
            <Typography variant="caption" sx={{ color: 'text.secondary', display: 'block', lineHeight: 1.1 }}>
              Saldo Warzone Coins
            </Typography>
            <Typography sx={{ fontWeight: 900, lineHeight: 1.2 }}>
              {isSaldoLoading && saldo === null
                ? 'Carregando…'
                : saldo === null
                  ? '—'
                  : formatCoins(Number(saldo ?? 0) || 0)}
            </Typography>
          </Box>
        </Box>

        <Box
          onClick={() => go('/dashboard/perfil')}
          role="button"
          tabIndex={0}
          onKeyDown={(e) => {
            if (e.key === 'Enter' || e.key === ' ') go('/dashboard/perfil')
          }}
          sx={{
            mt: 1.25,
            p: 1.25,
            borderRadius: 2,
            display: 'flex',
            alignItems: 'center',
            gap: 1.25,
            bgcolor: (perfil?.vipAtual?.diasRestantes ?? 0) > 0 ? 'rgba(255,109,0,0.12)' : 'rgba(255,255,255,0.04)',
            border:
              (perfil?.vipAtual?.diasRestantes ?? 0) > 0
                ? '1px solid rgba(255,109,0,0.28)'
                : '1px solid rgba(255,255,255,0.10)',
            cursor: 'pointer',
            userSelect: 'none',
            '&:hover': { bgcolor: (perfil?.vipAtual?.diasRestantes ?? 0) > 0 ? 'rgba(255,109,0,0.16)' : 'rgba(255,255,255,0.06)' }
          }}
        >
          <WorkspacePremium
            sx={{ fontSize: 20, color: (perfil?.vipAtual?.diasRestantes ?? 0) > 0 ? 'secondary.main' : 'text.secondary' }}
          />
          <Box sx={{ flex: 1, minWidth: 0 }}>
            <Typography variant="caption" sx={{ color: 'text.secondary', display: 'block', lineHeight: 1.1 }}>
              VIP
            </Typography>
            <Typography sx={{ fontWeight: 900, lineHeight: 1.2 }}>
              {(perfil?.vipAtual?.diasRestantes ?? 0) > 0
                ? `${Number(perfil?.vipAtual?.diasRestantes ?? 0).toLocaleString('pt-BR')} dia(s)`
                : 'Sem VIP'}
            </Typography>
          </Box>
        </Box>

        <Box sx={{ display: 'flex', gap: 1, mt: 2 }}>
          <Button
            variant="outlined"
            size="small"
            startIcon={<HomeIcon />}
            onClick={goHome}
            sx={{
              flex: 1,
              borderColor: 'rgba(255,255,255,0.14)',
              color: 'text.primary',
              '&:hover': { borderColor: 'primary.main', bgcolor: 'rgba(0,230,118,0.10)' }
            }}
          >
            Home
          </Button>
          <Button
            variant="outlined"
            size="small"
            startIcon={<ExitToApp />}
            onClick={doLogout}
            sx={{
              flex: 1,
              borderColor: 'rgba(255,255,255,0.14)',
              color: 'text.primary',
              '&:hover': { borderColor: 'secondary.main', bgcolor: 'rgba(255,109,0,0.10)' }
            }}
          >
            Sair
          </Button>
        </Box>
      </Box>

      <Divider sx={{ borderColor: 'rgba(255,255,255,0.08)' }} />

      <Box sx={{ flex: 1, overflow: 'auto' }}>
        <List sx={{ px: 2, py: 2 }}>
          {menuItems.map((item) => {
            const isActive = location.pathname === item.path
            return (
              <ListItem key={item.path} disablePadding sx={{ mb: 1 }}>
                <ListItemButton
                  onClick={() => go(item.path)}
                  sx={{
                    borderRadius: 2,
                    py: 1.2,
                    bgcolor: isActive ? 'rgba(0,230,118,0.18)' : 'transparent',
                    border: isActive ? '1px solid rgba(0,230,118,0.30)' : '1px solid transparent',
                    '&:hover': { bgcolor: 'rgba(0,230,118,0.10)' }
                  }}
                >
                  <ListItemIcon sx={{ minWidth: 40, color: isActive ? 'primary.main' : 'text.secondary' }}>
                    {item.icon}
                  </ListItemIcon>
                  <ListItemText
                    primary={item.text}
                    primaryTypographyProps={{ fontWeight: 800, color: 'text.primary' }}
                  />
                </ListItemButton>
              </ListItem>
            )
          })}
        </List>
      </Box>
    </Box>
  )

  return (
    <Box sx={{ display: 'flex', minHeight: '100dvh' }}>
      {isMobile && (
        <AppBar position="fixed" elevation={0}>
          <Toolbar>
            <IconButton color="inherit" edge="start" onClick={toggleDrawer} sx={{ mr: 1 }}>
              <MenuIcon />
            </IconButton>
            <Typography sx={{ fontWeight: 900 }}>Palworld • Warzone</Typography>
            <Box sx={{ ml: 'auto', display: 'flex', alignItems: 'center', gap: 1 }}>
              <Box
                component="img"
                src={WARZONE_COIN_IMAGE_URL}
                alt="Warzone Coin"
                sx={{ width: 18, height: 18, opacity: 0.95 }}
                loading="lazy"
              />
              <Typography sx={{ fontWeight: 900 }}>
                {isSaldoLoading && saldo === null ? '—' : saldo === null ? '—' : formatCoins(Number(saldo ?? 0) || 0)}
              </Typography>
              {(perfil?.vipAtual?.diasRestantes ?? 0) > 0 && (
                <Box
                  sx={{
                    ml: 1,
                    px: 1,
                    py: 0.35,
                    borderRadius: 999,
                    border: '1px solid rgba(255,109,0,0.30)',
                    bgcolor: 'rgba(255,109,0,0.14)'
                  }}
                >
                  <Typography variant="caption" sx={{ fontWeight: 900 }}>
                    VIP {Number(perfil?.vipAtual?.diasRestantes ?? 0).toLocaleString('pt-BR')}d
                  </Typography>
                </Box>
              )}
            </Box>
          </Toolbar>
        </AppBar>
      )}

      <Box component="nav" sx={{ width: { md: drawerWidth }, flexShrink: { md: 0 } }}>
        <Drawer
          variant={isMobile ? 'temporary' : 'permanent'}
          open={isMobile ? mobileOpen : true}
          onClose={toggleDrawer}
          ModalProps={{ keepMounted: true }}
          sx={{
            '& .MuiDrawer-paper': {
              width: drawerWidth,
              boxSizing: 'border-box',
              borderRight: '1px solid rgba(255,255,255,0.08)'
            }
          }}
        >
          {drawer}
        </Drawer>
      </Box>

      <Box
        component="main"
        sx={{
          flexGrow: 1,
          width: { md: `calc(100% - ${drawerWidth}px)` },
          pt: isMobile ? 9 : 0,
          minHeight: '100dvh'
        }}
      >
        <Outlet />
      </Box>
    </Box>
  )
}

export default UserDashboardLayout
